import type { ReactElement } from "react";

import { useIsFetching } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import {
    ColumnsSelectionButtonContainer,
    ColumnsSelectionContainer,
    type TriggerProps,
} from "app/pages/sales-intelligence/common-components/column-picker";
import {
    StyledInnerContainer,
    TableActionsPanel,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { findAppsQueryKeyFactory } from "app/pages/sales-intelligence/pages/find-apps/query/queryKeyFactory";
import { selectActiveAppSourceType } from "app/pages/sales-intelligence/store/find-apps/selectors";

import ExcelExportButtonContainer from "./ExcelExportButtonContainer";
import SelectionModalContainer from "./SelectionModal/SelectionModalContainer";

type Props = {
    areTableActionsDisabled: boolean;
    numberOfResults: number;
};

const FindAppsTableActionsContainer = ({ areTableActionsDisabled, numberOfResults }: Props) => {
    const activeAppSourceType = useSelector(selectActiveAppSourceType);

    const numberOfFetchingColumnConfigQueries = useIsFetching({
        queryKey: findAppsQueryKeyFactory.columnConfig(activeAppSourceType),
    });

    const renderSelectionTrigger = ({ onClick }: TriggerProps): ReactElement => (
        <ColumnsSelectionButtonContainer
            isDisabled={areTableActionsDisabled || numberOfFetchingColumnConfigQueries > 0}
            tooltipContent={translate("si.columns-selection.tooltip.change-columns")}
            onClick={onClick}
        />
    );

    return (
        <TableActionsPanel>
            {({ activate }) => (
                <>
                    <StyledInnerContainer>
                        <ExcelExportButtonContainer
                            isDisabled={areTableActionsDisabled}
                            numberOfResults={numberOfResults}
                            onActivate={activate}
                        />
                    </StyledInnerContainer>

                    <ColumnsSelectionContainer
                        renderSelectionTrigger={renderSelectionTrigger}
                        renderSelectionModal={SelectionModalContainer}
                    />
                </>
            )}
        </TableActionsPanel>
    );
};

export default FindAppsTableActionsContainer;
